import { gql, useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import { Field, Formik } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'

import Analytics from '../../analytics/Analytics'
import {
  brandsForCurrentRetailerAccountSelector,
  retailerIdsByBrandIdSelector,
} from '../../stores/app/selectors'

const BULK_CREATE_CONTENT_REQUEST = gql`
  mutation bulkCreateContentRequest($data: BulkCreateNoteData!) {
    bulkCreateNote(data: $data) {
      id
    }
  }
`

const RequestMoreCampaignsDialog = ({ brandId, ...props }) => {
  const brands = useSelector(brandsForCurrentRetailerAccountSelector).sort(
    (a, b) => a.name.localeCompare(b.name),
  )
  const retailerIdsByBrandId = useSelector(retailerIdsByBrandIdSelector)
  const [bulkCreateContentRequest] = useMutation(BULK_CREATE_CONTENT_REQUEST)
  const { enqueueSnackbar } = useSnackbar()
  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        isAllBrandsSelected: false,
        selectedBrandIds: brandId ? [brandId] : [],
        note: '',
      }}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true)
        const brandIds = values.isAllBrandsSelected
          ? brands.map((x) => x.id)
          : values.selectedBrandIds
        const retailerIds = []
        const randomId = Math.random().toString(36)

        for (const brandId of brandIds) {
          const retailerBrandId = retailerIdsByBrandId[brandId]
          if (retailerBrandId) {
            retailerIds.push(retailerBrandId)
          }
        }

        try {
          Analytics.track('Click', {
            type: 'Request More Organic Campaigns',
            campaigns: props.total,
            connectedBrands: brands.length,
            brandIds,
          })

          await bulkCreateContentRequest({
            variables: {
              data: {
                resource_type: 'retailers',
                resource_id: retailerIds,
                note: {
                  activity_type: 'campaign_request',
                  note: values.note,
                  source: `campaign_request ${randomId}`,
                },
              },
            },
          })

          enqueueSnackbar(I18n.t('requestMoreCampaigns.successMessage'), {
            variant: 'success',
          })

          actions.resetForm()
          props.onClose()
        } catch (err) {
          console.error(err)
          enqueueSnackbar(I18n.t('requestMoreCampaigns.errorMessage'), {
            variant: 'error',
          })
        }

        actions.setSubmitting(false)
      }}
    >
      {({ values, setFieldValue, handleSubmit, resetForm, isSubmitting }) => {
        const handleClose = () => {
          resetForm()
          props.onClose()
        }
        const handleToggleBrand = (brand) => {
          let nextValue

          if (values.selectedBrandIds.includes(brand.id)) {
            nextValue = values.selectedBrandIds.filter((x) => x !== brand.id)
          } else {
            nextValue = values.selectedBrandIds.concat(brand.id)
          }

          setFieldValue('selectedBrandIds', nextValue)
        }

        return (
          <Dialog
            fullWidth
            maxWidth="xs"
            onClose={handleClose}
            open={props.open}
          >
            <DialogTitle>{I18n.t('content.requestMore')}</DialogTitle>
            <DialogContent>
              <Field
                component={TextField}
                fullWidth
                label={I18n.t('requestMoreCampaigns.notePrompt')}
                multiline
                name="note"
                rows={3}
              />
              {!brandId && (
                <>
                  <Typography>
                    {I18n.t('requestMoreCampaigns.selectBrands')}
                  </Typography>
                  <List>
                    <ListItem
                      button
                      divider
                      onClick={() => {
                        setFieldValue(
                          'isAllBrandsSelected',
                          !values.isAllBrandsSelected,
                        )
                      }}
                    >
                      <Checkbox checked={values.isAllBrandsSelected} />
                      <ListItemText primary={I18n.t('nav.allBrands')} />
                    </ListItem>
                    {brands.map((brand, i) => (
                      <ListItem
                        button
                        disabled={values.isAllBrandsSelected}
                        divider={i < brands.length - 1}
                        key={`brand-${brand.name}`}
                        onClick={() => {
                          handleToggleBrand(brand)
                        }}
                      >
                        <Checkbox
                          checked={
                            values.isAllBrandsSelected ||
                            values.selectedBrandIds.includes(brand.id)
                          }
                        />
                        <ListItemText primary={brand.name} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>{I18n.t('shared.cancel')}</Button>
              <Button
                color="primary"
                disabled={
                  isSubmitting ||
                  (values.isAllBrandsSelected === false &&
                    values.selectedBrandIds.length === 0)
                }
                onClick={handleSubmit}
                variant="contained"
              >
                {I18n.t('requestMoreCampaigns.submit')}
              </Button>
            </DialogActions>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default RequestMoreCampaignsDialog
